import * as THREE from 'three'
import {
  createVector3,
  type GSplatVector3Input,
  writeVector3Array,
} from '@/plugins/gsplat/effects/effectUtils'
import {
  GSplatShaderEffect,
  type GSplatShaderEffectOptions,
} from '@/plugins/gsplat/effects/GSplatShaderEffect'

const shaderGLSL = /* glsl */ `
uniform vec3 uAabbMin;
uniform vec3 uAabbMax;
uniform float uEdgeScaleFactor;

void modifySplatCenter(inout vec3 center) {
}

void modifySplatCovariance(vec3 originalCenter, vec3 modifiedCenter, inout vec3 covA, inout vec3 covB) {
  bool insideAABB = all(greaterThanEqual(modifiedCenter, uAabbMin)) && all(lessThanEqual(modifiedCenter, uAabbMax));

  if (!insideAABB) {
    gsplatMakeRound(covA, covB, 0.0);
    return;
  }

  #ifdef GSPLAT_PRECISE_CROP
  float maxRadius = gsplatExtractSize(covA, covB) * 3.0;
  vec3 distToMin = modifiedCenter - uAabbMin;
  vec3 distToMax = uAabbMax - modifiedCenter;
  float minDist = min(
    min(min(distToMin.x, distToMin.y), distToMin.z),
    min(min(distToMax.x, distToMax.y), distToMax.z)
  );

  if (maxRadius > minDist) {
    float scale = (minDist / max(maxRadius, 0.0001)) * uEdgeScaleFactor;
    gsplatApplyUniformScale(covA, covB, scale);
  }
  #endif
}

void modifySplatColor(vec3 center, inout vec4 color) {
}
`

const shaderWGSL = /* wgsl */ `
fn modifySplatCenter(center: ptr<function, vec3f>) {
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let insideAABB = all(modifiedCenter >= uniform.uAabbMin) &&
    all(modifiedCenter <= uniform.uAabbMax);
  if (!insideAABB) {
    *scale = vec3f(0.0);
    return;
  }

  #ifdef GSPLAT_PRECISE_CROP
    let maxRadius = length(*scale) * 3.0;
    let distToMin = modifiedCenter - uniform.uAabbMin;
    let distToMax = uniform.uAabbMax - modifiedCenter;
    let minDist = min(
      min(min(distToMin.x, distToMin.y), distToMin.z),
      min(min(distToMax.x, distToMax.y), distToMax.z)
    );
    if (maxRadius > minDist) {
      let cropScale = (minDist / max(maxRadius, 0.0001)) * uniform.uEdgeScaleFactor;
      *scale = (*scale) * cropScale;
    }
  #endif
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
}
`

export interface GSplatCropShaderEffectOptions extends GSplatShaderEffectOptions {
  aabbMin?: GSplatVector3Input
  aabbMax?: GSplatVector3Input
  edgeScaleFactor?: number
}

export class GSplatCropShaderEffect extends GSplatShaderEffect {
  aabbMin: THREE.Vector3
  aabbMax: THREE.Vector3
  edgeScaleFactor: number

  private readonly _aabbMinArray = [0, 0, 0]
  private readonly _aabbMaxArray = [0, 0, 0]

  constructor(options: GSplatCropShaderEffectOptions = {}) {
    super(options)
    this.aabbMin = createVector3(options.aabbMin, new THREE.Vector3(-0.5, -0.5, -0.5))
    this.aabbMax = createVector3(options.aabbMax, new THREE.Vector3(0.5, 0.5, 0.5))
    this.edgeScaleFactor = options.edgeScaleFactor ?? 0.5
  }

  getShaderGLSL(): string {
    return shaderGLSL
  }

  getShaderWGSL(): string {
    return shaderWGSL
  }

  protected override updateEffect(): void {
    this.setUniform('uAabbMin', writeVector3Array(this.aabbMin, this._aabbMinArray))
    this.setUniform('uAabbMax', writeVector3Array(this.aabbMax, this._aabbMaxArray))
    this.setUniform('uEdgeScaleFactor', this.edgeScaleFactor)
  }
}
